import { Injectable } from "@angular/core";
import { TypeHelpers } from "@common/utils/type.helpers";
import { AppService } from "@ep-om/app.service";
import { combineLatest, Observable } from "rxjs";
import { distinctUntilChanged, map, shareReplay, startWith } from "rxjs/operators";
import { LastUpdateInfo, LastUpdateStatus } from "./lastUpdate.model";
import { LastUpdateQuery } from "./lastUpdate.query";
import { LastUpdateState, LastUpdateStore } from "./lastUpdate.store";

@Injectable({
  providedIn: 'root'
})
export class LastUpdateService {
  constructor(
    private _store: LastUpdateStore,
    public query: LastUpdateQuery,
    private appService: AppService,
  ) {
  }

  calculateChildKey(projectId: string, childStoreName: string) {
    return `PRJ_${projectId}_${childStoreName}`;
  }

  /**
   * Sets the Store in 'first_sync' state
   * @param storeName 
   * @param projectId 
   */
  setStoreInSync(storeName: string, projectId?: string): void {
    this._setStoreLiveOrInSync(storeName, 'first_sync', projectId);
  }

  /**
   * Sets the Store in 'live' state
   * @param storeName 
   * @param projectId 
   */
  setStoreLive(storeName: string, projectId?: string): void {
    this._setStoreLiveOrInSync(storeName, 'live', projectId);
  }

  private _setStoreLiveOrInSync(storeName: string, status: LastUpdateStatus, projectId?: string) {
    const key = projectId ? this.calculateChildKey(projectId, storeName) : storeName;
    this._store.update(state => ({
      ...state,
      [key]: {
        ...state[key] || {},
        status,
        remaining: status === 'live' ? 0 : state[key]?.remaining,
      }
    }))
  }

  isStoreLive$(storeName: string, projectId?: string) {
    const key = projectId ? this.calculateChildKey(projectId, storeName) : storeName;
    return combineLatest([
      this.appService.online$,
      this.query.select(key),
    ]).pipe(map(([online, val]) => online === true && val?.status === 'live'));
  }

  globalContentStatus$ = this.selectContentStatus((key) => this.isGlobalStoreKey(key));

  isGlobalContentLive$ = this.globalContentStatus$.pipe(
    map(status => status === 'live'),
    distinctUntilChanged(),
    shareReplay({ refCount: true, bufferSize: 1 }),
  );

  isEverythingLive$ = this.query.select().pipe(
    map(lastUpdates => !Object.values(lastUpdates).some(store => store?.status === null || store?.status === 'first_sync')),
    startWith(false),
    shareReplay({ refCount: true, bufferSize: 1 }),
  );

  isProjectContentLive$(projectId: string) {
    return this.projectContentStatus$(projectId).pipe(
      map(status => status === 'live'),
      distinctUntilChanged(),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );
  }

  projectContentStatus$(projectId: string): Observable<LastUpdateStatus> {
    const projectPrefix = `PRJ_${projectId}_`;
    return this.selectContentStatus((key) =>
      this.isGlobalStoreKey(key) || key.startsWith(projectPrefix),
    );
  }

  contentStatus$(projectId?: string): Observable<LastUpdateStatus> {
    return projectId ? this.projectContentStatus$(projectId) : this.globalContentStatus$;
  }

  showFirstSyncLoading$(projectId?: string): Observable<boolean> {
    return this.contentStatus$(projectId).pipe(
      map(status => status === 'first_sync'),
      distinctUntilChanged(),
    );
  }

  isStoreLive(storeName: string, projectId?: string) {
    const key = projectId ? this.calculateChildKey(projectId, storeName) : storeName;
    return this.appService.isOnline && this.query.getValue()[key]?.status === 'live';
  }

  setLastUpdate(storeName: string, updateStatus: TypeHelpers.WithOptional<Omit<LastUpdateInfo, 'status'>, 'remaining'>) {
    return this._store.update(state => ({
      ...state,
      [storeName]: {
        ...state[storeName] || { status: 'first_sync' },
        ...updateStatus,
      }
    }));
  }


  /**
   * Azzera il watermark (lastId/lastUpdate), l'ancora in-store (lastStoredId/lastStoredUpdate)
   * e il flag storeEmptied di un cursor mantenendo status/remaining.
   * buildCursors lo escludera' (filtra su lastId+lastUpdate) forzando il resync di quell'entita'.
   */
  clearCursor(key: string) {
    this._store.update(state => {
      const entry = state[key];
      if (!entry?.lastId && !entry?.lastUpdate && entry?.lastStoredId === undefined && entry?.storeEmptied === undefined) {
        return state;
      }
      const { lastId, lastUpdate, lastStoredId, lastStoredUpdate, storeEmptied, ...rest } = entry;
      return { ...state, [key]: rest };
    });
  }

  cleanChildLastUpdate(projectId: string) {
    const state = this._store.getValue();
    let newState = { ...state }
    for (const key in newState) {
      if (key.startsWith(`PRJ_${projectId}_`)) {
        delete newState[key];
      }
    }
    this._store._setState(newState);
  }

  setChildLastUpdate(projectId: string, childStoreName: string, updateStatus: TypeHelpers.WithOptional<Omit<LastUpdateInfo, 'status'>, 'remaining'>) {
    const key = this.calculateChildKey(projectId, childStoreName);
    this._store.update(state => ({
      ...state,
      [key]: {
        ...state[key] || { status: 'first_sync' },
        ...updateStatus,
      }
    })
    );
  }

  private selectContentStatus(includeStore: (key: string) => boolean): Observable<LastUpdateStatus> {
    return combineLatest([
      this.appService.online$,
      this.query.select(),
    ]).pipe(
      map(([online, lastUpdates]) => {
        if (online === false) {
          return 'offline' as LastUpdateStatus;
        }

        return this.calculateContentStatus(lastUpdates, includeStore);
      }),
      startWith((this.appService.isOnline ? 'first_sync' : 'offline') as LastUpdateStatus),
      distinctUntilChanged(),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );
  }

  private calculateContentStatus(
    lastUpdates: LastUpdateState,
    includeStore: (key: string) => boolean,
  ): LastUpdateStatus {
    const stores = Object.entries(lastUpdates)
      .filter(([key]) => includeStore(key))
      .map(([, store]) => store)
      .filter((store) => !!store);

    if (stores.some(store => store.status === null || store.status === 'first_sync')) {
      return 'first_sync';
    }

    if (stores.some(store => store.status === 'offline')) {
      return 'offline';
    }

    return 'live';
  }

  private isGlobalStoreKey(key: string): boolean {
    return !key.startsWith('PRJ_');
  }

}
